import { z } from 'zod';
import { CharacterSlotId } from './participant.ts';

/**
 * Board data contract for any board module and any renderer (2D SVG or 3D scene).
 * Coordinates are optional board units: x right, y up, z toward the camera (right-handed, metres at 1 unit = 1 m
 * for later Blender exports; a Blender Z-up export must be rotated by the importer). Orientation is yaw in degrees
 * around +y. Scene graphs, meshes, cameras and local camera movement stay client-side and are never gameplay state.
 */
export const Vec3 = z.object({ x: z.number(), y: z.number(), z: z.number() });
export type Vec3 = z.infer<typeof Vec3>;

export const BoardSpace = z.object({
  id: z.string().min(1),
  label: z.string().nullable(),
  /** Directed adjacency; branching paths are just multiple connections. Legality is a module rule. */
  connections: z.array(z.string()),
  position: Vec3.nullable(),
  orientation: z.object({ yawDeg: z.number() }).nullable(),
  /** Module-defined kind (e.g. `start`, `activity`, `plain`). Renderers may style by kind. */
  kind: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
});
export type BoardSpace = z.infer<typeof BoardSpace>;

export const BoardGraph = z.object({
  id: z.string(),
  units: z.literal('board-units'),
  spaces: z.record(z.string(), BoardSpace),
  startSpaceId: z.string(),
  meta: z.record(z.string(), z.unknown()),
});
export type BoardGraph = z.infer<typeof BoardGraph>;

/** Character identity (participant) is separate from its model asset and animation state. */
export const BoardToken = z.object({
  participantId: z.string(),
  displayName: z.string(),
  characterSlot: CharacterSlotId.nullable(),
  spaceId: z.string(),
  animation: z.enum(['idle', 'moving', 'celebrate']),
  connected: z.boolean(),
  isTurnHolder: z.boolean(),
});
export type BoardToken = z.infer<typeof BoardToken>;

export const BoardHighlight = z.object({
  spaceId: z.string(),
  kind: z.enum(['legal-move', 'launch', 'marker', 'destination', 'collectible']),
});
export type BoardHighlight = z.infer<typeof BoardHighlight>;

/** A control the renderer shows as a legible text button; pressing it sends the named module action with the payload. */
export const BoardControl = z.object({
  id: z.string(),
  label: z.string(),
  action: z.string(),
  payload: z.unknown(),
  enabled: z.boolean(),
  hint: z.string().nullable(),
});
export type BoardControl = z.infer<typeof BoardControl>;

/**
 * Renderer-facing board projection. Modules produce it; the client board renderers consume it.
 * Rules never live in the renderer.
 */
export const BoardView = z.object({
  graph: BoardGraph,
  tokens: z.array(BoardToken),
  highlights: z.array(BoardHighlight),
  controls: z.array(BoardControl),
  status: z.object({ title: z.string(), lines: z.array(z.string()) }),
  /** Board-owned progress data (test counters now; collectibles or points later are module decisions). */
  progress: z.record(z.string(), z.number()),
  round: z
    .object({
      number: z.number().int(),
      roster: z.array(z.string()),
      completed: z.array(z.string()),
      launched: z.boolean(),
    })
    .nullable(),
  lastRoll: z.object({ participantId: z.string(), value: z.number().int(), rollId: z.string() }).nullable(),
});
export type BoardView = z.infer<typeof BoardView>;

/** Emitted once per accepted movement; clients animate locally and reconcile to the canonical position. */
export const MoveEvent = z.object({
  moveId: z.string(),
  participantId: z.string(),
  from: z.string(),
  path: z.array(z.string()).min(1),
  to: z.string(),
  durationHintMs: z.number().int(),
});
export type MoveEvent = z.infer<typeof MoveEvent>;

/** Emitted once per accepted server-resolved roll. Clients animate; the value in state is canonical. */
export const DiceEvent = z.object({
  rollId: z.string(),
  participantId: z.string(),
  sides: z.number().int().min(2),
  value: z.number().int().min(1),
});
export type DiceEvent = z.infer<typeof DiceEvent>;

export function neighbours(graph: BoardGraph, spaceId: string): string[] {
  return graph.spaces[spaceId]?.connections ?? [];
}

export function isAdjacent(graph: BoardGraph, from: string, to: string): boolean {
  return neighbours(graph, from).includes(to);
}

/** Referential validation for a board graph: start space exists and every connection resolves. */
export function validateGraph(graph: BoardGraph): string[] {
  const problems: string[] = [];
  if (!graph.spaces[graph.startSpaceId]) problems.push(`startSpaceId ${graph.startSpaceId} is not a space`);
  for (const [key, space] of Object.entries(graph.spaces)) {
    if (space.id !== key) problems.push(`space key ${key} does not match id ${space.id}`);
    for (const c of space.connections) {
      if (!graph.spaces[c]) problems.push(`space ${space.id} connects to unknown space ${c}`);
    }
  }
  return problems;
}
